/**
 * GameChanger CSV pitching section schema.
 * Defines column names from GameChanger season stats export and maps them to app stat_key.
 * appStatKey null = no mapping in app (ignored on import).
 */

export interface GameChangerPitchingColumn {
  gcHeader: string;
  appStatKey: string | null;
  description?: string;
}

export const GAMECHANGER_PITCHING_SCHEMA: GameChangerPitchingColumn[] = [
  { gcHeader: 'IP', appStatKey: 'IP', description: 'Innings pitched' },
  { gcHeader: 'GP', appStatKey: null, description: 'Games pitched' },
  { gcHeader: 'GS', appStatKey: null, description: 'Games started' },
  { gcHeader: 'BF', appStatKey: 'BF', description: 'Batters faced' },
  { gcHeader: '#P', appStatKey: 'Pitches', description: 'Total pitches' },
  { gcHeader: 'W', appStatKey: 'W', description: 'Wins' },
  { gcHeader: 'L', appStatKey: 'L', description: 'Losses' },
  { gcHeader: 'SV', appStatKey: 'SV', description: 'Saves' },
  { gcHeader: 'SVO', appStatKey: null, description: 'Save opportunities' },
  { gcHeader: 'BS', appStatKey: null, description: 'Blown saves' },
  { gcHeader: 'SV%', appStatKey: null, description: 'Save percentage' },
  { gcHeader: 'H', appStatKey: 'H_Allowed', description: 'Hits allowed' },
  { gcHeader: 'R', appStatKey: 'R_Allowed', description: 'Runs allowed' },
  { gcHeader: 'ER', appStatKey: 'ER', description: 'Earned runs allowed' },
  { gcHeader: 'BB', appStatKey: 'BB_Allowed', description: 'Walks allowed' },
  { gcHeader: 'SO', appStatKey: 'K', description: 'Strikeouts' },
  { gcHeader: 'K-L', appStatKey: null, description: 'Strikeouts looking' },
  { gcHeader: 'HBP', appStatKey: 'HBP_Allowed', description: 'Hit batters' },
  { gcHeader: 'ERA', appStatKey: 'ERA', description: 'Earned run average' },
  { gcHeader: 'WHIP', appStatKey: null, description: 'Walks + hits per inning' },
  { gcHeader: 'LOB', appStatKey: null, description: 'Runners left on base' },
  { gcHeader: 'BK', appStatKey: 'Balk', description: 'Balks' },
  { gcHeader: 'PIK', appStatKey: null, description: 'Runners picked off' },
  { gcHeader: 'CS', appStatKey: null, description: 'Runners caught stealing' },
  { gcHeader: 'SB', appStatKey: null, description: 'Stolen bases allowed' },
  { gcHeader: 'SB%', appStatKey: null, description: 'Stolen base % allowed' },
  { gcHeader: 'WP', appStatKey: 'WP', description: 'Wild pitches' },
  { gcHeader: 'BAA', appStatKey: null, description: 'Opponent batting average' },
  { gcHeader: 'MPHFB', appStatKey: null, description: 'Fastball velocity' },
  { gcHeader: 'MPHCT', appStatKey: null, description: 'Cutter velocity' },
  { gcHeader: 'MPHCB', appStatKey: null, description: 'Curveball velocity' },
  { gcHeader: 'MPHSL', appStatKey: null, description: 'Slider velocity' },
  { gcHeader: 'MPHCH', appStatKey: null, description: 'Changeup velocity' },
  { gcHeader: 'MPHOS', appStatKey: null, description: 'Offspeed velocity' },
  { gcHeader: 'P/IP', appStatKey: null, description: 'Pitches per inning' },
  { gcHeader: 'P/BF', appStatKey: null, description: 'Pitches per batter faced' },
  { gcHeader: '<3%', appStatKey: null, description: '% batters 3 pitches or less' },
  { gcHeader: 'LOO', appStatKey: null, description: 'Leadoff out' },
  { gcHeader: '1ST2OUT', appStatKey: null, description: 'Innings 1st 2 batters out' },
  { gcHeader: '123INN', appStatKey: null, description: '1-2-3 innings' },
  { gcHeader: '<13', appStatKey: null, description: 'Innings 13 pitches or fewer' },
  { gcHeader: 'FIP', appStatKey: null, description: 'Fielding independent pitching' },
  { gcHeader: 'S%', appStatKey: null, description: 'Strike percentage' },
  { gcHeader: 'FPS%', appStatKey: null, description: 'First pitch strike %' },
  { gcHeader: 'FPSO%', appStatKey: null, description: 'FPS at-bats resulting in out' },
  { gcHeader: 'FPSW%', appStatKey: null, description: 'FPS at-bats resulting in walk' },
  { gcHeader: 'FPSH%', appStatKey: null, description: 'FPS at-bats resulting in hit' },
  { gcHeader: 'BB/INN', appStatKey: null, description: 'Walks per inning' },
  { gcHeader: '0BBINN', appStatKey: null, description: 'Zero-walk innings' },
  { gcHeader: 'BBS', appStatKey: null, description: 'Walks that score' },
  { gcHeader: 'LOBB', appStatKey: null, description: 'Leadoff walk' },
  { gcHeader: 'LOBBS', appStatKey: null, description: 'Leadoff walk that scored' },
  { gcHeader: 'SM%', appStatKey: null, description: 'Swing and miss %' },
  { gcHeader: 'K/BF', appStatKey: null, description: 'Strikeouts per batter faced' },
  { gcHeader: 'K/BB', appStatKey: null, description: 'Strikeouts per walk' },
  { gcHeader: 'WEAK%', appStatKey: null, description: 'Weakly hit %' },
  { gcHeader: 'HHB%', appStatKey: null, description: 'Hard hit ball %' },
  { gcHeader: 'GO/AO', appStatKey: null, description: 'Ground out / air out' },
  { gcHeader: 'HR', appStatKey: null, description: 'Home runs allowed' },
  { gcHeader: 'LD%', appStatKey: null, description: 'Line drive %' },
  { gcHeader: 'FB%', appStatKey: null, description: 'Fly ball %' },
  { gcHeader: 'GB%', appStatKey: null, description: 'Ground ball %' },
  { gcHeader: 'BABIP', appStatKey: null, description: 'Opponent BABIP' },
  { gcHeader: 'BA/RISP', appStatKey: null, description: 'Opponent BA/RISP' },
  // Pitch-type columns (FB, FBS, ... CT, CB, SL, CH, OS) - no app mapping
  { gcHeader: 'FB', appStatKey: null, description: 'Fastballs thrown' },
  { gcHeader: 'FBS', appStatKey: null, description: 'Fastballs for strikes' },
  { gcHeader: 'FBS%', appStatKey: null, description: 'Fastball strike %' },
  { gcHeader: 'FBSW%', appStatKey: null, description: 'Fastballs swung at %' },
  { gcHeader: 'FBSM%', appStatKey: null, description: 'Fastballs swung and missed %' },
  { gcHeader: 'CT', appStatKey: null, description: 'Cutters thrown' },
  { gcHeader: 'CTS', appStatKey: null, description: 'Cutters for strikes' },
  { gcHeader: 'CTS%', appStatKey: null, description: 'Cutter strike %' },
  { gcHeader: 'CTSW%', appStatKey: null, description: 'Cutters swung at %' },
  { gcHeader: 'CTSM%', appStatKey: null, description: 'Cutters swung and missed %' },
  { gcHeader: 'CB', appStatKey: null, description: 'Curveballs thrown' },
  { gcHeader: 'CBS', appStatKey: null, description: 'Curveballs for strikes' },
  { gcHeader: 'CBS%', appStatKey: null, description: 'Curveball strike %' },
  { gcHeader: 'CBSW%', appStatKey: null, description: 'Curveballs swung at %' },
  { gcHeader: 'CBSM%', appStatKey: null, description: 'Curveballs swung and missed %' },
  { gcHeader: 'SL', appStatKey: null, description: 'Sliders thrown' },
  { gcHeader: 'SLS', appStatKey: null, description: 'Sliders for strikes' },
  { gcHeader: 'SLS%', appStatKey: null, description: 'Slider strike %' },
  { gcHeader: 'SLSW%', appStatKey: null, description: 'Sliders swung at %' },
  { gcHeader: 'SLSM%', appStatKey: null, description: 'Sliders swung and missed %' },
  { gcHeader: 'CH', appStatKey: null, description: 'Changeups thrown' },
  { gcHeader: 'CHS', appStatKey: null, description: 'Changeups for strikes' },
  { gcHeader: 'CHS%', appStatKey: null, description: 'Changeup strike %' },
  { gcHeader: 'CHSW%', appStatKey: null, description: 'Changeups swung at %' },
  { gcHeader: 'CHSM%', appStatKey: null, description: 'Changeups swung and missed %' },
  { gcHeader: 'OS', appStatKey: null, description: 'Offspeed thrown' },
  { gcHeader: 'OSS', appStatKey: null, description: 'Offspeed for strikes' },
  { gcHeader: 'OSS%', appStatKey: null, description: 'Offspeed strike %' },
  { gcHeader: 'OSSW%', appStatKey: null, description: 'Offspeed swung at %' },
  { gcHeader: 'OSSM%', appStatKey: null, description: 'Offspeed swung and missed %' },
];
